import { faMessage } from '@fortawesome/pro-solid-svg-icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { Body, IconBadge, IconBadgeUse, Subhead, cn } from '@growtherapy/sprout-ui';

import {
  ChatDrawerWrapper as BaseChatDrawer,
  ChatDrawerWrapperProps as BaseChatDrawerProps,
  CannedMessages,
} from '../../components';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useTheme } from '../../hooks';
import {
  currentConversationAtom,
  currentConversationMessagesAtom,
  currentConversationParticipantNamesAtom,
  currentConversationSidAtom,
  isChatErrorAtom,
} from '../../session/state';
import { Theme } from '../../state';
import { ParticipantStatus } from '../../types';
import { useGetClientSafetyGuard } from '../../useGetClientSafetyGuard';
import { connectedAtAtom } from '../../video-sdk';
import { twilioIdentityAtom } from '../../video-sdk/twilio';
import {
  ProviderDrawerState,
  currentConversationPatientShortIdAtom,
  drawerStateAtom,
  numWaitingVisitorsAtom,
  sessionConversationReadyAtom,
} from '../state';
import { CANNED_MESSAGES, NO_PARTICIPANTS_MESSAGE, SAFETY_GUARD_BANNER_TEXT } from './constants';

function SessionChatPlaceholder() {
  return (
    <div className='flex flex-col gap-6 items-center h-full'>
      <IconBadge aria-hidden size='xl' icon={faMessage} use={IconBadgeUse.Lilac} className='text-[1.25rem]' />
      <section>
        <Subhead.Default as='h2' className='text-xlarge font-medium text-center mb-2 rebrand:text-text-inverse'>
          Messaging
        </Subhead.Default>
        <Body.Default className='text-neutral-700 self-center text-center rebrand:text-text-inverse'>
          Chat with clients here once they enter your waiting room.
        </Body.Default>
      </section>
    </div>
  );
}

export function ChatDrawer({
  drawerState,
  isEmptyState,
  isError,
  onSend,
  numberOfClientChats,
  onGoToChatList,
  ...otherProps
}: { drawerState?: ProviderDrawerState | null } & BaseChatDrawerProps & {
    numberOfClientChats: number;
  }) {
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  const isWaitingRoomChat = drawerState === ProviderDrawerState.WAITING_ROOM_CHAT;
  const isSessionChat = drawerState === ProviderDrawerState.SESSION_CHAT;
  const isParticipantDisconnectedChat = drawerState === ProviderDrawerState.PARTICIPANT_DISCONNECTED_CHAT;
  const chatStatus = isWaitingRoomChat ? ParticipantStatus.WAITING : ParticipantStatus.IN_SESSION;

  const onQuickReplySelect = (message: string) => {
    sendLoggingEvents(TrackingEvents.PROVIDER_TEMPLATE_MESSAGE_SEND);
    onSend(message);
  };

  const onMessageSend = (message: string) => {
    sendLoggingEvents(TrackingEvents.PROVIDER_MESSAGE_SEND);
    onSend(message);
  };

  const hasOtherAvailableChats = numberOfClientChats > 1 || (isParticipantDisconnectedChat && numberOfClientChats > 0);

  return (
    <BaseChatDrawer
      {...(hasOtherAvailableChats && { onGoToChatList })}
      {...(!isEmptyState && !isParticipantDisconnectedChat && { chatStatus })}
      headerText={
        isSessionChat && !isEmptyState ? 'Messages will persist only for the duration of the session' : undefined
      }
      emptyState={<SessionChatPlaceholder />}
      isEmptyState={isEmptyState}
      isError={isError}
      quickReplyComponent={
        isWaitingRoomChat ? (
          <CannedMessages
            messages={CANNED_MESSAGES}
            disabled={isError}
            onSelect={onQuickReplySelect}
            className={cn('px-8 pb-2 bg-neutral-400', {
              'rebrand:bg-surface-inverseEmphasis': isDarkTheme,
            })}
          />
        ) : undefined
      }
      sendIsDisabled={isParticipantDisconnectedChat}
      onSend={onMessageSend}
      footerText={isParticipantDisconnectedChat ? NO_PARTICIPANTS_MESSAGE : undefined}
      {...otherProps}
    />
  );
}

export function ChatDrawerWrapper() {
  const [drawerState, setDrawerState] = useAtom(drawerStateAtom);
  const numWaitingVisitors = useAtomValue(numWaitingVisitorsAtom);
  const sessionConversationReady = useAtomValue(sessionConversationReadyAtom);
  const numberOfClientChats = sessionConversationReady ? numWaitingVisitors + 1 : numWaitingVisitors;
  const connectedAt = useAtomValue(connectedAtAtom);
  const conversation = useAtomValue(currentConversationAtom);
  const messages = useAtomValue(currentConversationMessagesAtom);
  const identity = useAtomValue(twilioIdentityAtom);
  const patientShortId = useAtomValue(currentConversationPatientShortIdAtom);
  const { requiresSafetyGuards } = useGetClientSafetyGuard(patientShortId);
  const chatName = useAtomValue(currentConversationParticipantNamesAtom).join(', ');
  const isChatError = useAtomValue(isChatErrorAtom);
  const setCurrentConversationSid = useSetAtom(currentConversationSidAtom);
  const sendMessage = (message: string) => {
    sendLoggingEvents(TrackingEvents.ANY_MESSAGE_SEND);
    if (drawerState === ProviderDrawerState.WAITING_ROOM_CHAT) {
      sendLoggingEvents(TrackingEvents.ANY_WAITING_ROOM_MESSAGE_SEND);
    }
    if (drawerState === ProviderDrawerState.SESSION_CHAT) {
      sendLoggingEvents(TrackingEvents.ANY_SESSION_MESSAGE_SEND);
    }
    conversation?.sendMessage(message);
  };

  const onGoToChatList = () => {
    setDrawerState(ProviderDrawerState.ALL_CLIENTS_CHAT_LIST);
    setCurrentConversationSid(undefined);
  };

  return (
    <ChatDrawer
      onGoToChatList={onGoToChatList}
      onSend={sendMessage}
      isError={isChatError}
      warningBannerText={requiresSafetyGuards ? SAFETY_GUARD_BANNER_TEXT : undefined}
      isEmptyState={numWaitingVisitors === 0 && drawerState === ProviderDrawerState.WAITING_ROOM_CHAT}
      {...{
        chatName,
        connectedAt,
        drawerState,
        identity,
        messages,
        numberOfClientChats,
      }}
    />
  );
}
